import { useState, useEffect } from 'react';
import {
  Users,
  Coins,
  Sparkles,
  ImageIcon,
  Video,
  AlertTriangle,
  RefreshCw,
  Clock,
  ShoppingCart,
  MessageSquare,
  Crown,
} from 'lucide-react';
import { credits, CreditsDashboard } from '../../api/client';
import { formatNumber, cn } from '../../lib/utils';

export default function CreditsOverview() {
  const [data, setData] = useState<CreditsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const res = await credits.dashboard();
      setData(res);
    } catch (error) {
      console.error('Failed to fetch credits dashboard:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(), 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading credits dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient">Credits Overview</h1>
          <p className="text-muted-foreground mt-1">Credit system stats and activity</p>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm"
        >
          <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={formatNumber(data?.total_users ?? 0)}
          icon={Users}
          color="emerald"
          delay={100}
        />
        <StatCard
          title="Credits Granted"
          value={formatNumber(data?.total_credits_granted ?? 0)}
          icon={Coins}
          color="teal"
          delay={150}
        />
        <StatCard
          title="Total Purchases"
          value={formatNumber(data?.total_purchases ?? 0)}
          icon={ShoppingCart}
          color="cyan"
          delay={200}
        />
        <StatCard
          title="Credits Purchased"
          value={formatNumber(data?.total_credits_purchased ?? 0)}
          icon={Coins}
          color="violet"
          delay={250}
        />
        <StatCard
          title="Generations Today"
          value={formatNumber(data?.today.generations ?? 0)}
          icon={Sparkles}
          color="cyan"
          delay={300}
        />
        <StatCard
          title="Images Today"
          value={formatNumber(data?.today.images ?? 0)}
          icon={ImageIcon}
          color="violet"
          delay={350}
        />
        <StatCard
          title="Videos Today"
          value={formatNumber(data?.today.videos ?? 0)}
          icon={Video}
          color="amber"
          delay={400}
        />
        <StatCard
          title="Failed Today"
          value={formatNumber(data?.today.failed ?? 0)}
          icon={AlertTriangle}
          color="rose"
          delay={450}
        />
        <StatCard
          title="All Chats Today"
          value={formatNumber(
            (data?.today.text_messages ?? 0) +
            (data?.today.free_chat_messages ?? 0) +
            (data?.today.subscriber_messages ?? 0)
          )}
          icon={MessageSquare}
          color="teal"
          delay={475}
        />
        <StatCard
          title="Free Chat Today"
          value={formatNumber(data?.today.free_chat_messages ?? 0)}
          icon={MessageSquare}
          color="sky"
          delay={500}
        />
        <StatCard
          title="Subscriber Msgs Today"
          value={formatNumber(data?.today.subscriber_messages ?? 0)}
          icon={Crown}
          color="amber"
          delay={550}
        />
        <StatCard
          title="Active Subscribers"
          value={formatNumber(data?.total_subscriber_devices ?? 0)}
          icon={Crown}
          color="emerald"
          delay={600}
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Credit Flow */}
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up" style={{ animationDelay: '700ms' }}>
          <h2 className="text-lg font-semibold mb-4">Credit Flow</h2>
          <div className="space-y-4">
            <FlowRow label="Total Granted (admin)" value={data?.total_credits_granted ?? 0} color="emerald" />
            <FlowRow label="Total Purchased" value={data?.total_credits_purchased ?? 0} color="teal" />
            <FlowRow label="Total Used" value={data?.total_credits_used ?? 0} color="amber" />
            <FlowRow label="Total Refunded" value={data?.total_credits_refunded ?? 0} color="cyan" />
            <div className="border-t border-border/50 pt-3">
              <FlowRow
                label="Net Outstanding"
                value={(data?.total_credits_granted ?? 0) + (data?.total_credits_purchased ?? 0) - (data?.total_credits_used ?? 0)}
                color="teal"
                bold
              />
            </div>
          </div>
        </div>

        {/* Today's Activity */}
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up" style={{ animationDelay: '800ms' }}>
          <h2 className="text-lg font-semibold mb-4">Today's Activity</h2>
          <div className="space-y-4">
            <FlowRow label="Text Messages (paid)" value={data?.today.text_messages ?? 0} color="cyan" />
            <FlowRow label="Free Chat Messages" value={data?.today.free_chat_messages ?? 0} color="sky" />
            <FlowRow label="Subscriber Messages" value={data?.today.subscriber_messages ?? 0} color="amber" />
            <div className="border-t border-border/50 pt-3">
              <FlowRow
                label="All Chats (paid + free + subscriber)"
                value={(data?.today.text_messages ?? 0) + (data?.today.free_chat_messages ?? 0) + (data?.today.subscriber_messages ?? 0)}
                color="teal"
                bold
              />
            </div>
            <FlowRow label="Active Subscribers Today" value={data?.today.subscriber_devices ?? 0} color="emerald" />
            <FlowRow label="Images Generated" value={data?.today.images ?? 0} color="violet" />
            <FlowRow label="Videos Generated" value={data?.today.videos ?? 0} color="amber" />
            <FlowRow label="Purchases" value={data?.today.purchases ?? 0} color="teal" />
            <FlowRow label="Credits Consumed" value={data?.today.credits_consumed ?? 0} color="rose" />
            <FlowRow label="Credits Purchased" value={data?.today.credits_purchased ?? 0} color="emerald" />
            <FlowRow label="Credits Refunded" value={data?.today.credits_refunded ?? 0} color="emerald" />
          </div>
        </div>
      </div>

      {/* Active Reservations */}
      <div className="card-glow rounded-2xl p-6 animate-fade-in-up" style={{ animationDelay: '900ms' }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <Clock className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Active Reservations</h2>
            <p className="text-sm text-muted-foreground">
              {data?.active_reservations === 0
                ? 'No active reservations'
                : `${data?.active_reservations} reservation${(data?.active_reservations ?? 0) > 1 ? 's' : ''} currently held (not yet confirmed/refunded)`
              }
            </p>
          </div>
          {(data?.active_reservations ?? 0) > 0 && (
            <span className="ml-auto text-2xl font-bold text-amber-400">
              {data?.active_reservations}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const colorMap: Record<string, { bg: string; text: string; iconBg: string }> = {
  emerald: { bg: 'stat-card-emerald', text: 'text-emerald-400', iconBg: 'bg-emerald-500/20' },
  teal: { bg: '', text: 'text-teal-400', iconBg: 'bg-teal-500/20' },
  cyan: { bg: 'stat-card-cyan', text: 'text-cyan-400', iconBg: 'bg-cyan-500/20' },
  violet: { bg: 'stat-card-violet', text: 'text-violet-400', iconBg: 'bg-violet-500/20' },
  amber: { bg: 'stat-card-amber', text: 'text-amber-400', iconBg: 'bg-amber-500/20' },
  rose: { bg: '', text: 'text-rose-400', iconBg: 'bg-rose-500/20' },
  sky: { bg: '', text: 'text-sky-400', iconBg: 'bg-sky-500/20' },
};

function StatCard({
  title,
  value,
  icon: Icon,
  color,
  delay,
}: {
  title: string;
  value: string;
  icon: typeof Users;
  color: string;
  delay: number;
}) {
  const c = colorMap[color] || colorMap.emerald;
  return (
    <div
      className={cn('card-glow rounded-2xl p-6 animate-fade-in-up', c.bg)}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', c.iconBg)}>
          <Icon className={cn('w-5 h-5', c.text)} />
        </div>
      </div>
      <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
      <p className="text-3xl font-bold tracking-tight">{value}</p>
    </div>
  );
}

function FlowRow({
  label,
  value,
  color,
  bold,
}: {
  label: string;
  value: number;
  color: string;
  bold?: boolean;
}) {
  const c = colorMap[color] || colorMap.emerald;
  return (
    <div className="flex items-center justify-between">
      <span className={cn('text-sm', bold ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
        {label}
      </span>
      <span className={cn('font-mono text-sm', bold ? 'text-lg font-bold' : '', c.text)}>
        {formatNumber(value)}
      </span>
    </div>
  );
}
